import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, auth, userProfiles, rides as ridesAPI } from '../supabaseClient';

const AppContext = createContext();

const MOCK_USER = { id: 1, name: 'Arjun Singh', city: 'Pune', bike: 'RE Himalayan 450', avatar_url: 'AS', verified: true };

const MOCK_RIDES = [
  { id: 1, title: 'Lonavala Weekend Run', date: 'Apr 12, 2026', from: 'Pune', to: 'Lonavala', distance: '65 km', joined: 8, max: 12, bikeReq: 'All bikes', status: 'open', organizer: 'Ravi K.', desc: 'Chill Sunday morning ride to Lonavala. Breakfast stop at Café Della Lama. Return by noon.', tags: ['Beginner Friendly', 'Group Ride'] },
  { id: 2, title: 'Spiti Valley Expedition', date: 'May 5–12, 2026', from: 'Delhi', to: 'Spiti', distance: '1200 km', joined: 14, max: 15, bikeReq: '350cc+', status: 'full', organizer: 'Priya D.', desc: 'Full 8-day expedition through Spiti Valley. High altitude riding, camping nights, legendary views.', tags: ['Expert', 'Expedition', 'Camping'] },
  { id: 3, title: 'Coorg Coffee Trail', date: 'Apr 19, 2026', from: 'Bengaluru', to: 'Coorg', distance: '285 km', joined: 5, max: 10, bikeReq: 'All bikes', status: 'open', organizer: 'Kiran M.', desc: 'Ride through the coffee estates of Coorg. Two-night stay arranged at a homestay.', tags: ['Nature', 'Weekend', 'Stay Included'] },
  { id: 4, title: 'Mumbai → Goa Coastal', date: 'May 20–22, 2026', from: 'Mumbai', to: 'Goa', distance: '600 km', joined: 9, max: 12, bikeReq: 'All bikes', status: 'open', organizer: 'Sneha R.', desc: 'Iconic NH66 coastal highway. Sea views, fresh seafood stops, 3-day adventure.', tags: ['Coastal', 'Scenic', 'Beach'] },
  { id: 5, title: 'Ladakh Legendary Ride', date: 'Jun 1–10, 2026', from: 'Manali', to: 'Leh', distance: '1050 km', joined: 10, max: 10, bikeReq: '500cc+', status: 'full', organizer: 'Vijay T.', desc: 'The ultimate Indian biking bucket list. Khardung La pass, Pangong Lake, Nubra Valley.', tags: ['Bucket List', 'Expert', 'High Altitude'] },
  { id: 6, title: 'Sunday Breakfast Ride', date: 'Every Sunday', from: 'Pune', to: 'Khed Shivapur', distance: '40 km', joined: 22, max: 50, bikeReq: 'All bikes', status: 'open', organizer: 'MotoHub India Pune', desc: 'Casual weekly meetup. All riders welcome. No experience needed. Great chai and poha guaranteed.', tags: ['Weekly', 'Casual', 'Beginner'] },
];

const MOCK_MESSAGES = {
  general: [
    { id: 1, user: 'Ravi K.', avatar_url: 'RK', text: 'Anyone joining Lonavala this Sunday? 🔥', time: '9:02 AM', mine: false },
    { id: 2, user: 'Priya D.', avatar_url: 'PD', text: 'I\'m in! Leaving Katraj at 6am', time: '9:05 AM', mine: false },
    { id: 3, user: 'Arjun Singh', avatar_url: 'AS', text: 'Count me in. Riding the Himalayan 450 💪', time: '9:08 AM', mine: true },
    { id: 4, user: 'Kiran M.', avatar_url: 'KM', text: 'Which route - expressway or old highway?', time: '9:10 AM', mine: false },
    { id: 5, user: 'Ravi K.', avatar_url: 'RK', text: 'Old highway only! Expressway is boring for bikes', time: '9:12 AM', mine: false },
    { id: 6, user: 'Sneha R.', avatar_url: 'SR', text: 'Good call. Better views and zero trucks 🏔️', time: '9:14 AM', mine: false },
  ],
  spiti: [
    { id: 1, user: 'Priya D.', avatar_url: 'PD', text: 'Reminder: get your bike serviced before May 3rd', time: 'Yesterday', mine: false },
    { id: 2, user: 'Vijay T.', avatar_url: 'VT', text: 'Also carry extra engine oil. Altitude kills consumption', time: 'Yesterday', mine: false },
    { id: 3, user: 'Arjun Singh', avatar_url: 'AS', text: 'Got my Himalayan tuned yesterday. All set!', time: 'Today', mine: true },
  ],
  pune: [
    { id: 1, user: 'MB Pune', avatar_url: 'MP', text: 'Weekly ride this Sunday 6am, Katraj chowk', time: 'Today', mine: false },
    { id: 2, user: 'Rahul V.', avatar_url: 'RV', text: 'Will there be new riders? Bringing my cousin', time: 'Today', mine: false },
    { id: 3, user: 'MB Pune', avatar_url: 'MP', text: 'All welcome! We always have a sweep rider at the back', time: 'Today', mine: false },
  ],
};

const INITIAL_CHANNELS = [
  { id: 'general', name: 'General', icon: 'CHAT', desc: 'Open community chat', type: 'community' },
  { id: 'spiti', name: 'Spiti Expedition', icon: 'RIDE', desc: 'May 5-12 ride group', type: 'ride', rideId: 2 },
  { id: 'pune', name: 'Pune Chapter', icon: 'CITY', desc: 'Pune riders only', type: 'community' },
];

const BIKE_CATEGORIES = [
  { id: 1, icon: '🏔️', name: 'Adventure / Tourer', count: 2140, examples: ['RE Himalayan', 'KTM 390 Adv', 'BMW GS 310', 'Suzuki V-Strom'], color: '#E84C1E', desc: 'Built for long-distance touring on any terrain. Best for Ladakh, Spiti, and highway expeditions.' },
  { id: 2, icon: '🛣️', name: 'Cruiser', count: 1820, examples: ['Royal Enfield Bullet', 'RE Meteor 350', 'Bajaj Avenger', 'Honda H\'ness'], color: '#F59E0B', desc: 'Low and relaxed riding position. Perfect for highway cruising and casual long rides.' },
  { id: 3, icon: '⚡', name: 'Sports / Superbike', count: 980, examples: ['Yamaha R15', 'Honda CBR', 'KTM Duke 390', 'Kawasaki Ninja'], color: '#3B82F6', desc: 'Speed and performance focused. Great for track days and fast mountain passes.' },
  { id: 4, icon: '🏙️', name: 'Commuter', count: 3400, examples: ['Hero Splendor', 'Bajaj Pulsar', 'Honda Shine', 'TVS Apache'], color: '#22C55E', desc: 'Fuel-efficient and practical. Surprisingly great for shorter group rides.' },
  { id: 5, icon: '🔋', name: 'Electric', count: 560, examples: ['Ather 450X', 'Ola S1 Pro', 'TVS iQube', 'Bajaj Chetak'], color: '#A855F7', desc: 'The future of Indian riding. Growing community, perfect for city rides.' },
  { id: 6, icon: '🛵', name: 'Scooter', count: 1200, examples: ['Honda Activa', 'TVS Ntorq', 'Jupiter', 'Suzuki Access'], color: '#06B6D4', desc: 'Easy to ride and widely available. No gear changes, great for city explorers.' },
];

const ROUTES_DATA = [
  { id: 1, name: 'Manali → Leh Highway', distance: 1050, state: 'Himachal Pradesh', season: 'Jun–Sep', altitude: '5,400m', difficulty: 'hard', rating: 4.9, desc: 'The bucket list. World\'s highest motorable roads. Baralacha La, Lachulung La, Tanglang La passes. Pure legend.', highlights: ['Baralacha La Pass', 'Sarchu Plains', 'Tanglang La', 'Leh Palace'] },
  { id: 2, name: 'Bengaluru → Coorg', distance: 285, state: 'Karnataka', season: 'Year round', altitude: '1,525m', difficulty: 'easy', rating: 4.7, desc: 'Lush coffee estates, misty hills, and winding roads through Kodagu. Best after monsoon when it\'s green.', highlights: ['Abbey Falls', 'Coffee Estates', 'Dubare Elephant Camp', 'Talacauvery'] },
  { id: 3, name: 'Mumbai → Goa (NH66)', distance: 600, state: 'Maharashtra / Goa', season: 'Oct–Mar', altitude: 'Sea level', difficulty: 'medium', rating: 4.8, desc: 'India\'s most scenic coastal highway. Arabian Sea on one side, Western Ghats on the other. Endless beach stops.', highlights: ['Ganpatipule Beach', 'Ratnagiri Fort', 'Devbagh Beach', 'Palolem'] },
  { id: 4, name: 'Shimla → Spiti Valley', distance: 900, state: 'Himachal Pradesh', season: 'Jul–Sep', altitude: '4,550m', difficulty: 'hard', rating: 4.9, desc: 'Remote and raw. Spiti feels like a different planet. Monasteries, moonlike landscapes, and no mobile signal.', highlights: ['Kinnaur Valley', 'Kalpa', 'Key Monastery', 'Chandratal Lake'] },
  { id: 5, name: 'Pune → Lonavala', distance: 110, state: 'Maharashtra', season: 'Year round', altitude: '620m', difficulty: 'easy', rating: 4.5, desc: 'Perfect beginner route. Quick expressway or scenic old highway. Tiger Point viewpoint is unmissable.', highlights: ['Tiger Point', 'Bhushi Dam', 'Rajmachi Fort', 'Imagica Theme Park'] },
  { id: 6, name: 'Rishikesh → Chopta (Mini Switzerland)', distance: 230, state: 'Uttarakhand', season: 'Apr–Jun, Sep–Nov', altitude: '2,680m', difficulty: 'medium', rating: 4.6, desc: 'Meadows, dense oak forests, and the best views of Kedarnath. Called Mini Switzerland of India for good reason.', highlights: ['Tungnath Temple', 'Chandrashila Peak', 'Deoria Tal', 'Ukhimath'] },
];

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rides, setRides] = useState(MOCK_RIDES);
  const [messages, setMessages] = useState(MOCK_MESSAGES);
  const [channels, setChannels] = useState(INITIAL_CHANNELS);
  const [joinedRides, setJoinedRides] = useState([]);
  const [sosActive, setSosActive] = useState(false);

  // Load rides from database
  const loadRidesFromDatabase = async () => {
    try {
      const { data, error } = await ridesAPI.getAllRides();
      
      console.log('[Rides API] Response:', { data, error });
      
      if (error) {
        console.error('Error loading rides:', error);
        return;
      }
      
      if (data && data.length > 0) {
        console.log('[Rides] Raw data from DB:', data);
        
        // Map database rides to include all required fields for display
        const formattedRides = data.map(ride => {
          const distanceValue = ride.distance_km ?? ride.distance ?? null;
          const normalizedDistance =
            distanceValue === null || distanceValue === undefined || distanceValue === ''
              ? 'TBD'
              : `${distanceValue} km`;
          const formatted = {
            id: ride.id,
            title: ride.title || 'Untitled Ride',
            date: ride.date || 'TBD',
            from: ride.location?.split(' to ')[0]?.trim() || 'Unknown',
            to: ride.location?.split(' to ')[1]?.trim() || 'Unknown',
            distance: normalizedDistance,
            joined: ride.joined_count ?? ride.joined ?? 1,
            max: ride.max_riders ?? ride.max ?? 10,
            bikeReq: ride.bike_requirement || ride.bikeReq || 'All bikes',
            status: ride.status || 'open',
            organizer: ride.organizer || 'MotoHub India Rider',
            desc: ride.description || ride.desc || 'Community ride plan',
            tags: ride.tags || ['Community Planned', 'Open Invite'],
            created_at: ride.created_at,
            created_by: ride.created_by,
            user_id: ride.user_id,
          };
          console.log('[Rides] Formatted ride:', formatted);
          return formatted;
        });
        
        console.log('[Rides] Setting state with', formattedRides.length, 'rides');
        setRides(prev => [...formattedRides, ...prev.filter(r => !r.user_id || !formattedRides.find(f => f.id === r.id))]);
      } else {
        console.log('[Rides] No data returned from database');
      }
    } catch (error) {
      console.error('Error loading rides from database:', error);
    }
  };

  const syncUserProfile = async (authUser, profileFields = {}) => {
    if (!authUser) return false;

    try {
      const { data: existingProfile, error: profileError } = await userProfiles.getProfile(authUser.id);
      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error checking existing profile:', profileError);
        return false;
      }

      const resolvedName =
        profileFields.name?.trim() ||
        existingProfile?.name ||
        authUser.user_metadata?.name ||
        (authUser.email ? authUser.email.split('@')[0] : 'MotoHub India Rider');

      const resolvedCity =
        profileFields.city?.trim() ||
        existingProfile?.city ||
        authUser.user_metadata?.city ||
        'India';

      const resolvedBike =
        profileFields.bike?.trim() ||
        existingProfile?.bike ||
        authUser.user_metadata?.bike ||
        'My Bike';

      const resolvedPhone =
        profileFields.phone?.trim() ||
        authUser.phone ||
        existingProfile?.phone ||
        null;

      const resolvedRole = String(
        profileFields.role ||
          existingProfile?.role ||
          authUser.user_metadata?.role ||
          'rider'
      )
        .trim()
        .toLowerCase();

       const profileData = {
         id: authUser.id,
         name: resolvedName,
         city: resolvedCity,
         state: profileFields.state?.trim() || existingProfile?.state || authUser.user_metadata?.state || '',
         bike: resolvedBike,
         email: authUser.email || existingProfile?.email || profileFields.email || null,
         experience: profileFields.experience || existingProfile?.experience || 'Intermediate',
         preferred_ride: profileFields.preferred_ride || existingProfile?.preferred_ride || 'Adventure',
        avatar_url:
          existingProfile?.avatar_url ||
           resolvedName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
         phone: resolvedPhone,
         verified: authUser.email_confirmed_at ? true : existingProfile?.verified || false,
         username: profileFields.username?.trim().toLowerCase() || existingProfile?.username || null,
         bio: profileFields.bio?.trim().slice(0, 160) || existingProfile?.bio || '',
         instagram: profileFields.instagram?.trim() || existingProfile?.instagram || '',
         youtube: profileFields.youtube?.trim() || existingProfile?.youtube || '',
         riding_style: profileFields.riding_style || existingProfile?.riding_style || 'commuter',
         is_public: typeof profileFields.is_public === 'boolean' ? profileFields.is_public : existingProfile?.is_public ?? true,
         bikes_owned: profileFields.bikes_owned || existingProfile?.bikes_owned || [],
         role: resolvedRole || 'rider',
       };

      const { data, error } = await userProfiles.upsertProfile(profileData);
      if (error) {
        console.error('Error saving profile:', error);
        return false;
      }

      if (data) {
        setUser({
          id: data.id,
          name: data.name,
          city: data.city,
          state: data.state,
          bike: data.bike,
          email: data.email,
          experience: data.experience,
          preferred_ride: data.preferred_ride,
          avatar_url: data.avatar_url,
          verified: data.verified,
          phone: data.phone,
          username: data.username,
          bio: data.bio,
          instagram: data.instagram,
          youtube: data.youtube,
          riding_style: data.riding_style,
          is_public: data.is_public,
          bikes_owned: data.bikes_owned || [],
          role: data.role || 'rider',
        });
      }

      return true;
    } catch (error) {
      console.error('Profile sync error:', error);
      return false;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const getInitialSession = async () => {
      const { session } = await auth.getSession();
      if (session?.user) {
        await loadUserProfile(session.user.id, session.user);
      }
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setTimeout(() => {
            loadUserProfile(session.user.id, session.user);
            setLoading(false);
          }, 0);
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Load rides from database on mount
  useEffect(() => {
    const initRides = async () => {
      // Load database rides
      await loadRidesFromDatabase();
      // Keep mock rides as fallback
    };
    initRides();
  }, []);

  // Load user profile from database
  const loadUserProfile = async (userId, authUserOverride = null) => {
    try {
      const { data: profile, error } = await userProfiles.getProfile(userId);
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading profile:', error);
        return;
      }

      if (profile) {
        setUser({
          id: profile.id,
          name: profile.name,
          city: profile.city,
          state: profile.state,
          bike: profile.bike,
          avatar_url: profile.avatar_url,
          verified: profile.verified,
          phone: profile.phone,
          email: profile.email,
          experience: profile.experience,
          preferred_ride: profile.preferred_ride,
          username: profile.username,
          bio: profile.bio,
          instagram: profile.instagram,
          youtube: profile.youtube,
          riding_style: profile.riding_style,
          is_public: profile.is_public,
          bikes_owned: profile.bikes_owned || [],
          role: profile.role || 'rider',
        });
        return;
      }

      const authUser = authUserOverride || (await auth.getCurrentUser()).user;
      if (authUser) {
        await syncUserProfile(authUser);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  // Login function - now creates/updates user profile
  const login = async (profileFields = {}, authUserOverride = null) => {
    try {
      const authLookup = authUserOverride ? { user: authUserOverride, error: null } : await auth.getCurrentUser();
      const { user: authUser, error: authError } = authLookup;
      if (authError || !authUser) return false;
      return await syncUserProfile(authUser, profileFields);
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await auth.signOut();
      setUser(null);
      setJoinedRides([]);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUserProfile = async (updates = {}) => {
    try {
      const { user: authUser, error: authError } = await auth.getCurrentUser();
      if (authError || !authUser) return { success: false, error: "Not authenticated" };

      const success = await syncUserProfile(authUser, updates);
      if (!success) {
        return { success: false, error: "Profile could not be updated" };
      }

      return { success: true };
    } catch (error) {
      console.error("Update profile error:", error);
      return { success: false, error: error.message || "Profile could not be updated" };
    }
  };

  const createCommunityChannel = ({ name, desc, icon = 'CHAT' }) => {
    const id = `channel-${Date.now()}`;
    const channel = {
      id,
      name,
      icon,
      desc,
      type: 'community',
      createdBy: user?.name || 'MotoHub India',
    };
    const welcomeMessage = {
      id: Date.now(),
      user: channel.createdBy,
      avatar_url: user?.avatar_url || 'MB',
      text: `Welcome to ${name}. Start planning, ask questions, and invite riders in your city.`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      mine: false,
    };

    setChannels(prev => [channel, ...prev]);
    setMessages(prev => ({ ...prev, [id]: [welcomeMessage] }));
    return channel;
  };

  const createRidePlan = async (rideInput) => {
    const { user: authUser } = await auth.getCurrentUser();
    
    const id = Date.now();
    const ride = {
      id,
      title: rideInput.title,
      date: rideInput.date,
      from: rideInput.from,
      to: rideInput.to,
      distance: rideInput.distance || 'TBD',
      joined: 1,
      max: Number(rideInput.max) || 10,
      bikeReq: rideInput.bikeReq || 'All bikes',
      status: 'open',
      organizer: user?.name || 'MotoHub India Rider',
      desc: rideInput.desc || 'Community ride plan created from chat.',
      tags: rideInput.tags?.length ? rideInput.tags : ['Community Planned', 'Open Invite'],
    };

    // Save only columns that currently exist in rides table
    const dbRide = {
      title: ride.title,
      location: `${ride.from} to ${ride.to}`,
      date: ride.date,
      created_by: authUser?.id,
      user_id: authUser?.id,
      distance_km: (() => {
        const parsed = parseFloat(String(ride.distance || '').replace(/[^\d.]/g, ''));
        return Number.isFinite(parsed) ? parsed : null;
      })(),
      bike_requirement: ride.bikeReq,
      max_riders: ride.max,
      joined_count: ride.joined,
      status: ride.status,
      organizer: ride.organizer,
      description: ride.desc,
      tags: ride.tags,
    };

    try {
      const { data, error } = await ridesAPI.createRide(dbRide);
      if (error) {
        console.error('Error creating ride in database:', error);
        // Still update local state even if DB save fails
      } else if (data) {
        // Update ride with database ID
        ride.id = data.id;
      }
    } catch (err) {
      console.error('Error saving ride to database:', err);
    }

    const channelId = `ride-${id}`;
    const channel = {
      id: channelId,
      name: ride.title,
      icon: 'PLAN',
      desc: `${ride.from} to ${ride.to} · ${ride.date}`,
      type: 'ride',
      rideId: id,
      routeText: `${ride.from} to ${ride.to}`,
      date: ride.date,
    };

    const introMessages = [
      {
        id: id + 1,
        user: ride.organizer,
        avatar_url: user?.avatar_url || 'MB',
        text: `New ride plan created: ${ride.title}. Route: ${ride.from} to ${ride.to}.`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        mine: true,
      },
      {
        id: id + 2,
        user: 'MotoHub India',
        avatar_url: 'MB',
        text: `Ride date: ${ride.date} · Bike requirement: ${ride.bikeReq} · Slots: 1/${ride.max}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        mine: false,
      },
    ];

    setRides(prev => [ride, ...prev]);
    setChannels(prev => [channel, ...prev]);
    setMessages(prev => ({ ...prev, [channelId]: introMessages }));
    setJoinedRides(prev => (prev.includes(id) ? prev : [id, ...prev]));

    return { ride, channel };
  };

  const updateRidePlan = async (rideId, rideInput) => {
    const parsedDistance = parseFloat(String(rideInput.distance || '').replace(/[^\d.]/g, ''));
    const updateData = {
      title: rideInput.title,
      location: `${rideInput.from} to ${rideInput.to}`,
      date: rideInput.date,
      distance_km: Number.isFinite(parsedDistance) ? parsedDistance : null,
      bike_requirement: rideInput.bikeReq,
      max_riders: Number(rideInput.max) || 10,
      description: rideInput.desc || '',
    };

    const { data, error } = await ridesAPI.updateRide(rideId, updateData);
    if (error) return { data: null, error };

    setRides(prev =>
      prev.map(ride =>
        ride.id !== rideId
          ? ride
          : {
              ...ride,
              title: data?.title || rideInput.title,
              from: rideInput.from,
              to: rideInput.to,
              date: data?.date || rideInput.date,
              distance: rideInput.distance || ride.distance,
              max: Number(rideInput.max) || ride.max,
              bikeReq: rideInput.bikeReq || ride.bikeReq,
              desc: rideInput.desc || ride.desc,
            }
      )
    );

    return { data, error: null };
  };

  const deleteRidePlan = async (rideId) => {
    const { error } = await ridesAPI.deleteRide(rideId);
    if (error) return { error };

    setRides(prev => prev.filter(ride => ride.id !== rideId));
    setJoinedRides(prev => prev.filter(id => id !== rideId));
    setChannels(prev => prev.filter(channel => channel.rideId !== rideId));
    return { error: null };
  };

  const joinRide = (rideId) => {
    if (joinedRides.includes(rideId)) return;
    setJoinedRides(prev => [...prev, rideId]);
    setRides(prev => prev.map(r => {
      if (r.id !== rideId) return r;
      const nextJoined = r.joined + 1;
      return {
        ...r,
        joined: nextJoined,
        status: nextJoined >= r.max ? 'full' : r.status,
      };
    }));

    const rideChannel = channels.find(channel => channel.rideId === rideId);
    if (rideChannel) {
      const joinMessage = {
        id: Date.now(),
        user: user?.name || 'New Rider',
        avatar_url: user?.avatar_url || 'ME',
        text: `Joined this ride plan and is ready for ${rides.find(r => r.id === rideId)?.title || 'the ride'}.`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        mine: true,
      };
      setMessages(prev => ({
        ...prev,
        [rideChannel.id]: [...(prev[rideChannel.id] || []), joinMessage],
      }));
    }
  };

  const leaveRide = (rideId) => {
    if (!joinedRides.includes(rideId)) return;
    setJoinedRides(prev => prev.filter(id => id !== rideId));
    setRides(prev => prev.map(r => {
      if (r.id !== rideId) return r;
      const nextJoined = Math.max(0, r.joined - 1);
      return {
        ...r,
        joined: nextJoined,
        status: nextJoined < r.max ? 'open' : r.status,
      };
    }));

    const rideChannel = channels.find(channel => channel.rideId === rideId);
    if (rideChannel) {
      const leaveMessage = {
        id: Date.now(),
        user: user?.name || 'Rider',
        avatar_url: user?.avatar_url || 'ME',
        text: `Stepped out of this ride plan for now.`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        mine: true,
      };
      setMessages(prev => ({
        ...prev,
        [rideChannel.id]: [...(prev[rideChannel.id] || []), leaveMessage],
      }));
    }
  };

  const sendMessage = (channel, text) => {
    const newMsg = { id: Date.now(), user: user?.name || 'You', avatar_url: user?.avatar_url || 'ME', text, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), mine: true };
    setMessages(prev => ({ ...prev, [channel]: [...(prev[channel] || []), newMsg] }));
  };

  return (
    <AppContext.Provider value={{ user, loading, login, logout, updateUserProfile, rides, joinRide, leaveRide, joinedRides, messages, channels, sendMessage, createCommunityChannel, createRidePlan, updateRidePlan, deleteRidePlan, sosActive, setSosActive, BIKE_CATEGORIES, ROUTES_DATA }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
